import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { useWorkspace } from "@/context/WorkspaceContext";
import { WorkspaceRole } from "@/types/workspace";
import { normalizeUrl, validateUrl } from "@/lib/utils";
import {
  getOnboardingStatus,
  type OnboardingStep,
  type OnboardingStatus,
  updateOnboardingStatus,
} from "@/services/onboardingService";
import { projectsInstance } from "@/services/ProjectsService";
import { redirectToStripeWithReturnPath } from "@/lib/stripeNavigation";
import { Clock3, LogOut as IconLogOut, ShieldCheck, Sparkles } from "lucide-react";
import { OnboardingProgressDisplay } from "@/components/onboarding/OnboardingProgressDisplay";
import { CompetitorFavicon, VbButton, VbError } from "@/components/vb";
import { countBuyersInCache, useEnsureCompanyContext } from "@/hooks/vb/useBuyers";
import { useTriggerAnalysis } from "@/hooks/vb/useBuyers";
import { useAnalysis, useQuestions } from "@/hooks/vb/useBuyers";
import { PersonaStep } from "@/pages/vb/buyer-create/PersonaStep";
import { PickerSkeleton } from "@/pages/vb/buyer-create/PickerSkeleton";
import { QuestionsStep } from "@/pages/vb/buyer-create/QuestionsStep";
import { RunProviders } from "@/pages/vb/run/RunProviders";
import { RunActivityLog } from "@/pages/vb/run/RunActivityLog";
import { parseRunProgress } from "@/pages/vb/run/runProgress";
import { extractVbError } from "@/pages/vb/buyer-create/errors";
import { useBuyerWizard } from "@/pages/vb/buyer-create/useBuyerWizard";
import {
  trackCheckoutStarted,
  trackOnboardingStepViewed,
  trackVirtualBuyerCreated,
} from "@/lib/posthog";
import "@/styles/onboarding-progress.css";
import "@/styles/vb.css";

const MONTHLY_10_LINK = import.meta.env.VITE_STRIPE_V2_MONTHLY_10 || "";
const MONTHLY_30_LINK = import.meta.env.VITE_STRIPE_V2_MONTHLY_30 || "";
const MONTHLY_DIRECT_LINK = import.meta.env.VITE_STRIPE_V2_MONTHLY || "";
const ANNUAL_DIRECT_LINK = import.meta.env.VITE_STRIPE_V2_ANNUALLY || "";

type BillingCadence = "monthly" | "annual";

const STEP_ORDER: Record<Exclude<OnboardingStep, "signup" | "complete">, number> = {
  domain: 0,
  billing: 1,
  buyer: 2,
  questions: 3,
  simulation: 4,
};

const normalizeStep = (step: OnboardingStep | null | undefined): OnboardingStep => {
  if (!step || step === "signup") return "domain";
  return step;
};

const getDisplayDomain = (domain: string | null): string => {
  if (!domain) return "";
  try {
    const parsed = new URL(domain.includes("://") ? domain : `https://${domain}`);
    return parsed.hostname;
  } catch {
    return domain;
  }
};

export default function OnboardingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { token, user, logout } = useAuth();
  const { activeWorkspace, workspaces, setActiveWorkspace } = useWorkspace();

  // During onboarding the active workspace may be one the user was invited into.
  // All report/buyer operations must land in a workspace the user owns.
  const onboardingWorkspace = useMemo(
    () => workspaces.find((w) => w.role === WorkspaceRole.Owner) ?? activeWorkspace,
    [workspaces, activeWorkspace],
  );

  // Switch the global active workspace to the user's owned one so the
  // X-Workspace-ID header on every API call (buyer creation, analysis trigger,
  // etc.) targets the correct tenant, not a workspace they were invited into.
  useEffect(() => {
    if (
      onboardingWorkspace &&
      activeWorkspace &&
      onboardingWorkspace.id !== activeWorkspace.id
    ) {
      setActiveWorkspace(onboardingWorkspace);
    }
  }, [onboardingWorkspace, activeWorkspace, setActiveWorkspace]);

  const [status, setStatus] = useState<OnboardingStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [infoNotice, setInfoNotice] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [domain, setDomain] = useState("");
  const [cadence, setCadence] = useState<BillingCadence>("monthly");
  const [advancingBuyer, setAdvancingBuyer] = useState(false);
  const [advancingQuestions, setAdvancingQuestions] = useState(false);
  const [startingSimulation, setStartingSimulation] = useState(false);
  // Which destination is being finished, so the busy label lands on the button
  // that was actually clicked rather than always on the first one.
  const [finishingOnboarding, setFinishingOnboarding] = useState<"report" | "buyer" | null>(null);
  const [deferringOnboarding, setDeferringOnboarding] = useState(false);
  const lastTrackedStepRef = useRef<OnboardingStep | null>(null);

  const refreshStatus = async () => {
    const next = await getOnboardingStatus();
    setStatus(next);
    setDomain(getDisplayDomain(next.domain));
    return next;
  };

  useEffect(() => {
    const redirectState = location.state as { onboardingNotice?: unknown } | null;
    const notice =
      redirectState && typeof redirectState.onboardingNotice === "string"
        ? redirectState.onboardingNotice.trim()
        : "";

    if (!notice) return;

    setInfoNotice(notice);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const next = await refreshStatus();
        if (!mounted) return;
        if (next.onboarding_deferred) {
          navigate("/", { replace: true });
          return;
        }
        if (next.resume_key === "complete") {
          navigate("/", { replace: true });
        }
      } catch (err) {
        if (!mounted) return;
        setError("Unable to load onboarding state right now.");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [navigate]);

  const currentStep = useMemo(() => {
    return normalizeStep(status?.resume_key);
  }, [status]);

  const isVirtualBuyerStep =
    currentStep === "buyer" || currentStep === "questions" || currentStep === "simulation";

  useEffect(() => {
    if (currentStep !== "billing" && !isVirtualBuyerStep) return;
    if (status?.onboarding_report_completed) return;

    const intervalId = window.setInterval(() => {
      refreshStatus().catch(() => {
        // Keep the existing UI state if a periodic refresh fails.
      });
    }, 10000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [currentStep, status?.onboarding_report_completed]);

  const monthlyTrialLink =
    status?.billing_offer_code === "v2_monthly_30" ? MONTHLY_30_LINK : MONTHLY_10_LINK;
  const trialDays = status?.billing_offer_code === "v2_monthly_30" ? 30 : 10;

  const shouldShowProgress = currentStep === "billing" || isVirtualBuyerStep;

  const stepIndex = currentStep === "complete" ? 4 : STEP_ORDER[currentStep];

  const fallbackCompanyPath = useMemo(() => {
    const companyId = status?.company_id;
    const buyerId = status?.buyer_id;
    const simulationId = status?.simulation_id;

    if (currentStep === "simulation" && buyerId && simulationId) {
      return `/virtual-buyer/${buyerId}/run/${simulationId}?new=1&onboarding=1`;
    }
    if ((currentStep === "questions" || currentStep === "simulation") && buyerId) {
      return `/virtual-buyer/${buyerId}`;
    }
    if (companyId) {
      return `/virtual-buyer/new?company=${companyId}`;
    }
    return "/virtual-buyer";
  }, [currentStep, status?.buyer_id, status?.company_id, status?.simulation_id]);

  const onboardingCompanyId = status?.company_id ?? null;
  const onboardingCompany = useMemo(() => {
    if (onboardingCompanyId == null) return null;
    for (const ws of workspaces) {
      const c = ws.companies.find((x) => x.company_id === onboardingCompanyId);
      if (c) return c;
    }
    return null;
  }, [onboardingCompanyId, workspaces]);
  const buyerWizard = useBuyerWizard(
    onboardingCompanyId ?? 0,
    "onboarding_v2",
    status?.company_name ?? onboardingCompany?.company_name ?? null,
  );
  const qc = useQueryClient();
  // Only ensure the company context once we're actually in the Virtual Buyer
  // steps. `company_id` lands at the domain step, so an ungated hook fires this
  // during billing, where the Stripe hand-off (`window.location.assign`) aborts
  // it mid-flight — it can win the per-URL inference lock, then leave the
  // background prewarm waiting on a DB write that its own cancellation
  // guaranteed would never come. The prewarm queued by the domain step covers
  // the checkout window instead.
  const buyerContext = useEnsureCompanyContext(
    isVirtualBuyerStep ? onboardingCompanyId ?? undefined : undefined,
  );
  const triggerAnalysis = useTriggerAnalysis("onboarding_v2");
  const runBuyerId = status?.buyer_id ?? buyerWizard.buyerId ?? undefined;
  const runAnalysisId = status?.simulation_id ?? undefined;
  const runAnalysis = useAnalysis(runBuyerId, runAnalysisId, { poll: true });
  const runQuestions = useQuestions(runBuyerId);

  useEffect(() => {
    if (
      !user ||
      !status ||
      loading ||
      currentStep === "signup" ||
      lastTrackedStepRef.current === currentStep
    ) return;

    trackOnboardingStepViewed({
      user_id: user.id,
      step: currentStep,
      company_id: onboardingCompanyId,
      company_name: status.company_name ?? onboardingCompany?.company_name,
    });
    lastTrackedStepRef.current = currentStep;
  }, [
    currentStep,
    loading,
    onboardingCompany?.company_name,
    onboardingCompanyId,
    status,
    user,
  ]);

  // AITS-545 · the onboarding run's terminal events (run_completed / run_failed)
  // are emitted server-side on finalize (buyer/services/run_events.py). This is
  // the case that made the server-side move necessary: onboarding polls the first
  // run inline and hands off to the verdict/report before it finishes, so no
  // client ever witnessed the completion and it silently dropped. The backend
  // witnesses the finalize regardless.

  // AITS-545 · onboarding is a buyer's first run, so emit virtual_buyer_created
  // here (before run_started) with its real provenance from the wizard — the same
  // finalization timing as the in-app wizard. Deduped per buyer by the tracker.
  const emitOnboardingBuyerCreated = (buyerId: number) => {
    trackVirtualBuyerCreated({
      buyerId,
      origin: buyerWizard.buyerOrigin,
      companyId: onboardingCompanyId,
      companyName: status?.company_name ?? onboardingCompany?.company_name ?? null,
      virtualBuyerCount: Math.max(countBuyersInCache(qc), 1),
      source: "onboarding_v2",
    });
  };

  const handleBuyerContinue = async () => {
    setError("");
    if (!buyerWizard.personaComplete) {
      setError("Complete buyer name, description, and market region before continuing.");
      return;
    }
    setAdvancingBuyer(true);
    try {
      const buyerId = await buyerWizard.ensureBuyer();
      if (buyerId == null) return;
      const next = await updateOnboardingStatus({
        step: "questions",
        buyer_id: buyerId,
      });
      setStatus(next);
    } catch (err) {
      setError(`Could not save buyer setup: ${extractVbError(err).detail}`);
    } finally {
      setAdvancingBuyer(false);
    }
  };

  const handleQuestionsContinue = async () => {
    setError("");
    if (!buyerWizard.hasQuestions) {
      setError("Generate at least one question before continuing.");
      return;
    }

    setAdvancingQuestions(true);
    try {
      const buyerId = buyerWizard.buyerId ?? (await buyerWizard.ensureBuyer());
      if (buyerId == null) return;

      let simulationId = status?.simulation_id ?? null;
      if (simulationId == null) {
        try {
          emitOnboardingBuyerCreated(buyerId);
          const shadow = await triggerAnalysis.mutateAsync({
            buyerId,
            questionsCount: buyerWizard.questionCount || runQuestions.data?.length,
          });
          simulationId = shadow.id;
        } catch (err) {
          const vbError = extractVbError(err);
          if (vbError.status === 409) {
            const existingId = (err as { response?: { data?: { existingAnalysisId?: number } } })
              .response?.data?.existingAnalysisId;
            if (existingId != null) {
              simulationId = existingId;
            } else {
              throw err;
            }
          } else {
            throw err;
          }
        }
      }

      const next = await updateOnboardingStatus({
        step: "simulation",
        buyer_id: buyerId,
        simulation_id: simulationId,
      });
      setStatus(next);
    } catch (err) {
      setError(`Could not save questions setup: ${extractVbError(err).detail}`);
    } finally {
      setAdvancingQuestions(false);
    }
  };

  const handleSimulationContinue = async () => {
    setError("");

    const persistedBuyerId = status?.buyer_id ?? null;
    const localBuyerId = buyerWizard.buyerId ?? null;
    const buyerId = persistedBuyerId ?? localBuyerId;

    if (buyerId == null) {
      setError("Buyer setup must be completed before running the simulation.");
      return;
    }

    setStartingSimulation(true);
    try {
      let simulationId = status?.simulation_id ?? null;
      if (simulationId == null) {
        try {
          emitOnboardingBuyerCreated(buyerId);
          const shadow = await triggerAnalysis.mutateAsync({
            buyerId,
            questionsCount: buyerWizard.questionCount || runQuestions.data?.length,
          });
          simulationId = shadow.id;
        } catch (err) {
          const vbError = extractVbError(err);
          if (vbError.status === 409) {
            const existingId = (err as { response?: { data?: { existingAnalysisId?: number } } })
              .response?.data?.existingAnalysisId;
            if (existingId != null) {
              simulationId = existingId;
            } else {
              throw err;
            }
          } else {
            throw err;
          }
        }
      }

      const next = await updateOnboardingStatus({
        step: "simulation",
        buyer_id: buyerId,
        simulation_id: simulationId,
      });
      setStatus(next);
    } catch (err) {
      setError(`Could not start simulation: ${extractVbError(err).detail}`);
    } finally {
      setStartingSimulation(false);
    }
  };

  /**
   * Where onboarding hands off (AITS-540). Setup produces both an AI Trust Signals
   * report and a Virtual Buyer run, so the last step lets the user pick which
   * one to open rather than assuming.
   *
   * Virtual Buyer opens the verdict for the run they just watched — that's the
   * result they were waiting on — and falls back to the buyer list if the run
   * didn't produce one. No `?onboarding=1`: that flag makes VerdictPage
   * re-PATCH the completion we've already sent, and exempts the route from the
   * shell's onboarding gate it no longer needs exempting from.
   */
  const completionPath = (destination: "report" | "buyer"): string => {
    const companyId = status?.company_id;
    if (destination === "report") {
      return companyId ? `/reports/${companyId}` : "/";
    }
    if (runBuyerId && runAnalysisId) {
      return `/virtual-buyer/${runBuyerId}/verdict/${runAnalysisId}`;
    }
    return companyId ? `/virtual-buyer?company=${companyId}` : "/virtual-buyer";
  };

  const handleFinishOnboarding = async (destination: "report" | "buyer") => {
    setError("");
    if (!runBuyerId || !runAnalysisId) {
      setError("Simulation setup is still being prepared. Please wait a moment.");
      return;
    }
    setFinishingOnboarding(destination);
    try {
      await updateOnboardingStatus({
        step: "complete",
        buyer_id: runBuyerId,
        simulation_id: runAnalysisId,
      });
      if (user) {
        const companyName =
          status?.company_name ?? onboardingCompany?.company_name ?? null;
        trackOnboardingStepViewed({
          user_id: user.id,
          step: "complete",
          company_id: onboardingCompanyId,
          company_name: companyName,
        });
      }
      navigate(completionPath(destination));
    } catch (err) {
      setError(`Could not complete onboarding: ${extractVbError(err).detail}`);
    } finally {
      setFinishingOnboarding(null);
    }
  };

  const goStripe = (
    link: string,
    analytics: Parameters<typeof trackCheckoutStarted>[0],
  ) => {
    if (!link) {
      setError("Selected payment option is not configured yet.");
      return;
    }

    trackCheckoutStarted(analytics);
    redirectToStripeWithReturnPath(link, {
      email: user?.email ?? null,
      // Send Stripe back to the home page rather than a hardcoded onboarding
      // step: the shell re-checks onboarding status on every route change, so
      // `/` resolves correctly whether or not the payment webhook has settled,
      // and the same payment link also works from Settings.
      returnPath: "/",
      clientReferenceId: status?.client_reference_id ?? null,
    });
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const handleStayGuest = async () => {
    setError("");
    setDeferringOnboarding(true);
    try {
      const next = await updateOnboardingStatus({ defer_onboarding: true });
      setStatus(next);
      navigate("/", { replace: true });
    } catch {
      setError("Could not switch back to guest mode right now.");
    } finally {
      setDeferringOnboarding(false);
    }
  };

  const logoutButton = (
    <button
      type="button"
      onClick={() => {
        void handleLogout();
      }}
      title="Log out"
      className="fixed bottom-4 left-4 z-20 flex items-center gap-2 rounded px-2 py-2 text-sm text-red-600 hover:bg-red-50"
    >
      <IconLogOut size={16} />
      <span>Log out</span>
    </button>
  );

  const stayGuestButton = status?.can_defer && !isVirtualBuyerStep ? (
    <button
      type="button"
      onClick={() => {
        void handleStayGuest();
      }}
      disabled={deferringOnboarding}
      title="Back to app (stay a guest)"
      className="fixed bottom-14 left-4 z-20 flex items-center gap-2 rounded px-2 py-2 text-sm text-black hover:bg-gray-100 disabled:opacity-60"
    >
      <span>{deferringOnboarding ? "Returning..." : "Back to app (stay a guest)"}</span>
    </button>
  ) : null;

  const handleStartReport = async () => {
    setError("");
    if (!token) {
      setError("Please sign in again.");
      return;
    }
    if (!onboardingWorkspace?.id) {
      setError("Workspace is still loading. Please retry in a moment.");
      return;
    }

    const urlValidationError = validateUrl(domain);
    if (urlValidationError) {
      setError(urlValidationError);
      return;
    }

    const normalizedUrl = normalizeUrl(domain);
    const displayDomain = getDisplayDomain(normalizedUrl);

    setSubmitting(true);
    try {
      const createResult = await projectsInstance.createReport(
        displayDomain,
        normalizedUrl,
        false,
        token,
        onboardingWorkspace.id,
        "onboarding_v2",
      );

      if (!createResult.success || !createResult.reportId) {
        setError(createResult.message || "Could not start your report yet.");
        return;
      }

      const next = await updateOnboardingStatus({
        step: "billing",
        domain: normalizedUrl,
        report_id: createResult.reportId,
      });
      setStatus(next);
      setDomain(displayDomain);
    } catch {
      setError("Could not start your report yet.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-vb-bg px-4 py-8">
        <div className="mx-auto max-w-3xl rounded-2xl border border-gray-200 bg-white p-8">
          <p className="text-sm text-gray-500">Loading onboarding...</p>
        </div>
        {logoutButton}
      </div>
    );
  }

  if (currentStep === "complete") {
    return (
      <div className="min-h-screen bg-vb-bg px-4 py-8">
        <div className="mx-auto max-w-3xl rounded-2xl border border-gray-200 bg-white p-8 space-y-4">
          <h1 className="text-2xl font-semibold text-gray-900">You are all set</h1>
          <p className="text-sm text-gray-600">
            Billing is confirmed. Continue to your dashboard.
          </p>
          <Button onClick={() => navigate("/")}>Go to dashboard</Button>
        </div>
        {logoutButton}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-vb-bg px-4 py-8 md:px-6">
      {logoutButton}
      <div
        className={`mx-auto flex w-full flex-col items-center gap-4 ${
          isVirtualBuyerStep ? "max-w-[1100px]" : "max-w-4xl"
        }`}
      >
        <img
          src="/AITrustSignalLogoBlue.svg"
          alt="AI Trust Signals"
          className="h-6 w-auto"
        />

        <div className="flex items-center gap-2">
          {[0, 1, 2, 3, 4].map((index) => (
            <span
              key={index}
              className={`h-2 rounded-full transition-all ${
                index === stepIndex
                  ? "w-8 bg-vb-brand"
                  : index < stepIndex
                    ? "w-2 bg-vb-brand-dim"
                    : "w-2 bg-vb-line-strong"
              }`}
            />
          ))}
        </div>

        <span className="text-xs font-bold uppercase tracking-[0.14em] text-gray-500">
          Step {stepIndex + 1} of 5
        </span>

        <section className={`w-full ${isVirtualBuyerStep ? "max-w-[1100px]" : "max-w-2xl"}`}>
          {/* Progress display shows while building or completed (polling keeps data fresh) */}
          {shouldShowProgress && (
            <OnboardingProgressDisplay
              reportCompleted={Boolean(status?.onboarding_report_completed)}
              reportCreatedTs={status?.report_created_ts}
              fallbackDomain={domain}
              doneSubtitle={
                currentStep === "billing"
                  ? "Full access starts today. Choose your plan below to get started."
                  : "Your trust report is ready — you can view it from the sidebar when you're done here."
              }
            />
          )}

          <div className="mb-6 text-center">
            <h1 className="text-3xl font-semibold tracking-tight text-gray-900">
              {currentStep === "domain"
                ? "What does AI say when buyers ask about you?"
                : currentStep === "billing"
                  ? "Your report is already running"
                  : currentStep === "buyer"
                    ? "Who is your typical buyer?"
                    : currentStep === "questions"
                      ? "The questions your buyer would ask"
                      : "Run the virtual buyer"}
            </h1>
            <p className="mx-auto mt-3 max-w-xl text-sm font-medium leading-6 text-gray-600">
              {currentStep === "domain"
                ? "Enter your domain and your report starts building now. By the time you finish setup, your answers will be waiting."
                : currentStep === "billing"
                  ? "Pick how you want to open it when it's ready. Either way, full access starts today."
                  : currentStep === "buyer"
                    ? "Marcus drafts the first buyer from your company context. You can edit before continuing."
                    : currentStep === "questions"
                      ? "Generate and fine-tune Big-5 questions before running the simulation."
                      : "Run the buyer simulation across models and then continue to your dashboard."}
            </p>
          </div>

          {infoNotice ? (
            <div className="mb-5 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-700">
              {infoNotice}
            </div>
          ) : null}

          {error ? (
            <div className="mb-5 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {error}
            </div>
          ) : null}

          {currentStep === "domain" ? (
            <div>
              <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                <label className="flex items-center overflow-hidden rounded-lg border border-gray-300 bg-white focus-within:border-vb-brand focus-within:ring-[3px] focus-within:ring-vb-focus">
                  <span className="border-r border-gray-200 bg-gray-50 px-3 py-3 font-mono text-sm font-semibold text-gray-500">
                    https://
                  </span>
                  <Input
                    value={domain}
                    onChange={(event) => setDomain(event.target.value)}
                    placeholder="yourcompany.com"
                    className="h-12 rounded-none border-0 shadow-none focus-visible:ring-0"
                  />
                </label>
              </div>

              <div className="mt-4 flex justify-end">
                <Button
                  disabled={submitting}
                  onClick={handleStartReport}
                  className="h-11 bg-vb-brand px-6 transition hover:brightness-90"
                >
                  {submitting ? "Starting report..." : "Start my report"}
                </Button>
              </div>

              <p className="mt-4 flex items-center justify-center gap-2 text-sm font-medium text-gray-600">
                <Clock3 className="h-4 w-4 text-gray-500" />
                Your report starts the moment you continue, and keeps building while you finish setup.
              </p>
              <p className="mt-2 text-center text-xs text-gray-500">
                10,000+ small businesses scanned so far.
              </p>
            </div>
          ) : currentStep === "billing" ? (
            <div className="space-y-5">
              <div className="flex justify-center">
                <div className="inline-flex rounded-full border border-gray-300 bg-gray-100 p-1">
                  <button
                    className={`rounded-full px-4 py-2 text-sm font-semibold ${
                      cadence === "monthly" ? "bg-white text-gray-900 shadow" : "text-gray-600"
                    }`}
                    onClick={() => setCadence("monthly")}
                    type="button"
                  >
                    Monthly
                  </button>
                  <button
                    className={`rounded-full px-4 py-2 text-sm font-semibold ${
                      cadence === "annual" ? "bg-white text-gray-900 shadow" : "text-gray-600"
                    }`}
                    onClick={() => setCadence("annual")}
                    type="button"
                  >
                    Annual · 2 months free
                  </button>
                </div>
              </div>

              {/* Plan card — gradient border matches PricingPlanBox popular variant */}
              <div className="relative p-[3px] rounded-2xl bg-gradient-to-r from-vb-brand to-vb-accent-teal-bright shadow-xl">
                {/* FULL ACCESS badge — right-aligned, vertically centred on the card border */}
                <div className="absolute -top-[10px] right-10 bg-gradient-to-r from-vb-brand to-vb-accent-teal-bright text-white text-xs font-bold px-3 py-[3px] rounded-full shadow-md whitespace-nowrap">
                  FULL ACCESS
                </div>

                <div className="bg-white rounded-2xl p-6 flex flex-col gap-5">
                  {/* Price */}
                  <div className="pb-4 border-b">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">AI Trust Signals</h3>
                    <div className="flex flex-wrap items-end gap-x-2 gap-y-1">
                      <span
                        className={`text-4xl font-bold text-gray-900 ${
                          cadence === "annual" ? "font-mono" : ""
                        }`}
                      >
                        {cadence === "annual" ? "$250" : `${trialDays} Days Free!`}
                      </span>
                      <span className="pb-1 text-sm text-gray-600">
                        {cadence === "annual"
                          ? "/ year · 2 months free"
                          : "$25 a mo. after · cancel any time"}
                      </span>
                    </div>
                  </div>

                  {/* Feature list */}
                  <div>
                    <h5 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3">
                      What's included:
                    </h5>
                    <ul className="space-y-2 text-sm text-gray-700">
                      {[
                        "Your score, plus the list of what's holding it down",
                        "MarcusAI tells you what to fix first",
                        "See how ChatGPT, Gemini and Claude each describe you",
                        "Test every type of buyer you sell to",
                        "A fresh report every month, so you can watch your score climb",
                      ].map((feature) => (
                        <li key={feature} className="flex items-start gap-2">
                          <span className="text-blue-500 mt-px">✓</span>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* CTAs */}
                  {cadence === "monthly" ? (
                    <div className="space-y-2">
                      <Button
                        onClick={() =>
                          goStripe(monthlyTrialLink, {
                            cta: "start_trial",
                            link_variant:
                              status?.billing_offer_code === "v2_monthly_30"
                                ? "monthly_30d"
                                : "monthly_10d",
                            plan: "v2_monthly",
                            billing_interval: "monthly",
                            company_id: onboardingCompanyId,
                            company_name:
                              status?.company_name ?? onboardingCompany?.company_name,
                          })
                        }
                        className="h-11 w-full bg-vb-brand transition hover:brightness-90 font-bold"
                      >
                        {`Start my ${trialDays}-day free trial`}
                      </Button>
                      <p className="text-center text-xs text-gray-500">
                        Full access today. Your card is not charged until trial ends. Cancel before then and you pay nothing.
                      </p>
                      <Button
                        variant="outline"
                        onClick={() =>
                          goStripe(MONTHLY_DIRECT_LINK, {
                            cta: "subscribe_monthly",
                            link_variant: "monthly_direct",
                            plan: "v2_monthly",
                            billing_interval: "monthly",
                            company_id: onboardingCompanyId,
                            company_name:
                              status?.company_name ?? onboardingCompany?.company_name,
                          })
                        }
                        className="h-11 w-full border-gray-300 font-bold hover:bg-gray-100 hover:text-gray-900"
                      >
                        Or subscribe now: $25/mo
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Button
                        onClick={() =>
                          goStripe(ANNUAL_DIRECT_LINK, {
                            cta: "subscribe_annual",
                            link_variant: "annual",
                            plan: "v2_annual",
                            billing_interval: "annual",
                            company_id: onboardingCompanyId,
                            company_name:
                              status?.company_name ?? onboardingCompany?.company_name,
                          })
                        }
                        className="h-11 w-full bg-vb-brand transition hover:brightness-90 font-bold"
                      >
                        Subscribe now: $250/yr
                      </Button>
                      <p className="text-center text-xs text-gray-500">
                        Free trial available on monthly billing.
                      </p>
                    </div>
                  )}

                  <p className="text-center text-xs text-gray-500">
                    Your report keeps building while you check out.
                  </p>
                </div>
              </div>

              <p className="flex items-center justify-center gap-2 text-sm text-gray-600">
                <ShieldCheck className="h-4 w-4 text-green-600" />
                Secure checkout via Stripe. Cancel any time.
              </p>
            </div>
          ) : currentStep === "buyer" ? (
            <div className="vb-root rounded-vb-lg p-4 sm:p-6">
              <div className="space-y-5">
              {onboardingCompanyId == null ? (
                <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm space-y-4">
                  <p className="text-sm text-gray-600">
                    Buyer setup needs an onboarding company. Start the report first so onboarding can attach this step to a company.
                  </p>
                </div>
              ) : !buyerContext.isSuccess && !buyerContext.isError ? (
                <div className="vb-wizard">
                  <div className="persona-hd">
                    <div className="left">
                      <span className="tag">Buyer</span>
                      <h3 className="persona-hd__title">
                        Setting up your buyer for{" "}
                        {onboardingCompany?.company_name ? (
                          <span className="persona-hd__co">
                            <CompetitorFavicon
                              name={onboardingCompany.company_name}
                              url={onboardingCompany?.company_url ?? ""}
                              size={18}
                            />
                            <span className="persona-hd__co-name">{onboardingCompany.company_name}</span>
                          </span>
                        ) : (
                          "your company"
                        )}
                      </h3>
                      <div className="desc vb-context-loading__line">
                        <Sparkles size={14} aria-hidden="true" />
                        Reading {onboardingCompany?.company_name ?? "your company's"} website so the questions fit this buyer. This takes a few seconds the first time.
                      </div>
                    </div>
                  </div>
                  <PickerSkeleton rails={4} lines={3} />
                </div>
              ) : buyerContext.isError ? (
                <div className="vb-wizard">
                  <VbError
                    title={`We couldn't set up ${onboardingCompany?.company_name ?? "this company"}`}
                    description="We couldn't read the company website to tailor this buyer. Check the company URL is reachable, then try again."
                    onRetry={() => {
                      void buyerContext.refetch();
                    }}
                  />
                </div>
              ) : (
                <>
                  <PersonaStep
                    w={buyerWizard}
                    companyName={onboardingCompany?.company_name ?? ""}
                    companyUrl={onboardingCompany?.company_url ?? ""}
                  />
                  <div className="flex justify-end">
                    <Button
                      onClick={() => {
                        void handleBuyerContinue();
                      }}
                      disabled={
                        !buyerWizard.personaComplete ||
                        buyerWizard.suggesting ||
                        buyerWizard.generating ||
                        advancingBuyer
                      }
                      className="h-11 bg-vb-brand px-6 transition hover:brightness-90"
                    >
                      {advancingBuyer ? "Saving buyer..." : "Continue to questions"}
                    </Button>
                  </div>
                </>
              )}
              </div>
            </div>
          ) : currentStep === "questions" ? (
            <div className="vb-root rounded-vb-lg p-4 sm:p-6">
              <div className="space-y-5">
              {onboardingCompanyId == null ? (
                <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm space-y-4">
                  <p className="text-sm text-gray-600">
                    Questions setup needs an onboarding company. Start the report first so onboarding can attach this step to a company.
                  </p>
                </div>
              ) : !buyerWizard.personaComplete && buyerWizard.buyerId == null ? (
                <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm space-y-4">
                  <p className="text-sm text-gray-600">
                    We need your buyer persona details before generating questions.
                  </p>
                  <Button
                    onClick={() => {
                      navigate(fallbackCompanyPath);
                    }}
                    className="h-11 bg-vb-brand px-6 transition hover:brightness-90"
                  >
                    Back to buyer setup
                  </Button>
                </div>
              ) : (
                <>
                  <QuestionsStep
                    w={buyerWizard}
                    onRegenerate={() => {
                      void buyerWizard.generateQuestions();
                    }}
                  />
                  <div className="flex justify-end">
                    <Button
                      onClick={() => {
                        void handleQuestionsContinue();
                      }}
                      disabled={
                        !buyerWizard.hasQuestions ||
                        buyerWizard.generating ||
                        buyerWizard.suggesting ||
                        buyerWizard.addingQuestion ||
                        advancingQuestions
                      }
                      className="h-11 bg-vb-brand px-6 transition hover:brightness-90"
                    >
                      {advancingQuestions ? "Starting simulation..." : "Run simulation"}
                    </Button>
                  </div>
                </>
              )}
              </div>
            </div>
          ) : currentStep === "simulation" ? (
            <div className="vb-root rounded-vb-lg p-4 sm:p-6">
              <div className="onb-run space-y-5">
                {!status?.simulation_id ? (
                  <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm space-y-4">
                    <p className="text-sm text-gray-600">
                      Launch your virtual buyer simulation to score how AI answers the questions your buyers care about most.
                    </p>
                    <div className="flex flex-wrap gap-3">
                      <Button
                        onClick={() => {
                          void handleSimulationContinue();
                        }}
                        disabled={startingSimulation || triggerAnalysis.isPending}
                        className="h-11 bg-vb-brand px-6 transition hover:brightness-90"
                      >
                        {startingSimulation || triggerAnalysis.isPending ? "Starting simulation..." : "Run simulation"}
                      </Button>
                    </div>
                  </div>
                ) : runAnalysis.isError ? (
                  <div className="rounded-2xl border border-red-200 bg-red-50 p-6 shadow-sm space-y-3">
                    <p className="text-sm text-red-700">
                      We could not load simulation status right now.
                    </p>
                    <Button
                      variant="outline"
                      onClick={() => {
                        void runAnalysis.refetch();
                      }}
                    >
                      Retry
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm space-y-4">
                      <div className="vb-run-head">
                        <h1>
                          Asking AI about {onboardingCompany?.company_name ?? "your company"}...
                        </h1>
                        <p>This usually takes a minute or two. You can proceed once you're ready.</p>
                      </div>

                      <div className="vb-run-card">
                        <RunProviders legs={parseRunProgress(runAnalysis.data?.progress).legs} />

                        <div>
                          <div className="vb-run-prog-head">
                            <span className="vb-run-prog-head__label">
                              {(() => {
                                const prog = parseRunProgress(runAnalysis.data?.progress);
                                const qCount = runQuestions.data?.length ?? 0;
                                const modelCount = prog.legs.length || 3;
                                return qCount > 0
                                  ? `Asking questions · ${qCount} × ${modelCount} models`
                                  : `Asking the buyer's questions across ${modelCount} providers`;
                              })()}
                            </span>
                            <span className="pct" aria-hidden="true">
                              {runAnalysis.data?.status === "DONE"
                                ? 100
                                : parseRunProgress(runAnalysis.data?.progress).overall}
                              %
                            </span>
                          </div>
                          <div
                            className="vb-run-bar"
                            role="progressbar"
                            aria-label="Simulation progress"
                            aria-valuenow={
                              runAnalysis.data?.status === "DONE"
                                ? 100
                                : parseRunProgress(runAnalysis.data?.progress).overall
                            }
                            aria-valuemin={0}
                            aria-valuemax={100}
                          >
                            <span
                              style={{
                                width: `${
                                  runAnalysis.data?.status === "DONE"
                                    ? 100
                                    : parseRunProgress(runAnalysis.data?.progress).overall
                                }%`,
                              }}
                            />
                          </div>
                        </div>

                        <RunActivityLog
                          key={runAnalysisId ?? 0}
                          legs={parseRunProgress(runAnalysis.data?.progress).legs}
                          questions={runQuestions.data ?? []}
                        />
                      </div>
                    </div>

                    <div className="rounded-vb-lg border border-vb-line bg-vb-surface p-5 sm:p-6">
                      <h2 className="text-[18px] font-semibold text-vb-text">
                        Where do you want to start?
                      </h2>
                      <p className="mt-1 text-sm text-vb-text-2">
                        Setup produced both — you can open the other one from the sidebar
                        at any time.
                      </p>
                      <div className="mt-4 flex flex-col gap-3 sm:flex-row">
                        <VbButton
                          onClick={() => {
                            void handleFinishOnboarding("report");
                          }}
                          disabled={finishingOnboarding !== null}
                          size="lg"
                        >
                          {finishingOnboarding === "report"
                            ? "Finishing setup..."
                            : "AI Trust Signals report"}
                        </VbButton>
                        <VbButton
                          variant="secondary"
                          onClick={() => {
                            void handleFinishOnboarding("buyer");
                          }}
                          disabled={finishingOnboarding !== null}
                          size="lg"
                        >
                          {finishingOnboarding === "buyer"
                            ? "Finishing setup..."
                            : "Virtual Buyer results"}
                        </VbButton>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          ) : (
            <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm space-y-4">
              <p className="text-sm text-gray-600">
                This onboarding step is now recognized by routing and resume-state. In this cycle,
                it hands off to the existing Virtual Buyer flow while we embed the full in-onboarding
                experience in the next cycles.
              </p>
              <div className="flex flex-wrap gap-3">
                <Button
                  onClick={() => navigate(fallbackCompanyPath)}
                  className="h-11 bg-vb-brand px-6 transition hover:brightness-90"
                >
                  Continue in Virtual Buyer
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    void refreshStatus();
                  }}
                  className="h-11"
                >
                  Refresh status
                </Button>
              </div>
            </div>
          )}
        </section>
      </div>
      {stayGuestButton}
    </div>
  );
}
